import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v2, v2Unscoped, v2api, idempotencyKey, type V2Envelope } from './v2'
import { receiptKeys } from './receipts'
import type { CartPreviewRequest } from './cart'
import { useSessionStore } from '@/stores/sessionStore'
import { getElectronShell } from '@/lib/electronShell'
import type { Receipt } from './receipts'

type CartItems = CartPreviewRequest['items']

// Verified against project/journal/models/payments.py — PaymentType constants
export const PAYMENT_TYPE_IDS = {
  CASH: 211,
  MASTER_CARD: 213,
  POINTS: 214,
  DEBIT: 215,
  VISA: 216,
  AMEX: 217,
  GIFT_CARD: 251,
  STORE_CREDIT: 260,
  SPRINGBIG_POINTS: 299,
} as const

// `card` was historically just the balance scalar (v1 `check_balance`).
// The v2 handler returns the full GiftCard row after the Birchmount sync,
// so we accept both. Use `extractGiftCardBalance` / `extractGiftCardType`
// to read fields without caring which shape came back.
export interface GiftCardBalanceResponse {
  status: 'ok'
  card: string | number | GiftCardRecord
}

export interface GiftCardRecord {
  /** GiftCard row PK. /cart/commit and /receipts/{id}/payments require
   *  this for type-251 redemption — the legacy pay() looks up the row by
   *  id, not by number. Returned by /gift_cards/check_balance. */
  id?: number
  balance: string | number
  /** GiftCardType enum: 0 = Barnet (local-only), 1 = Birchmount. */
  type?: number
  status?: number
  number?: string
}

export function extractGiftCardBalance(card: GiftCardBalanceResponse['card']): number {
  if (typeof card === 'number') return card
  if (typeof card === 'string') return parseFloat(card) || 0
  const raw = card?.balance
  if (typeof raw === 'number') return raw
  return parseFloat(String(raw ?? '')) || 0
}

export function extractGiftCardId(card: GiftCardBalanceResponse['card']): number | null {
  if (typeof card !== 'object' || card === null) return null
  const id = card.id
  return typeof id === 'number' ? id : null
}

export function isBirchmountCard(card: GiftCardBalanceResponse['card']): boolean {
  return typeof card === 'object' && card !== null && card.type === 1
}

// ============================================================================
// V2 Payment Endpoints
// ============================================================================
// JWT-based payment endpoints with store_id and register_id in the URL path,
// constructed via the central `v2()` helper. Every payment-creating POST
// carries an `idempotency_key` so a network-level retry of the same logical
// attempt is de-duplicated server-side instead of double-charging.

interface V2PaymentRequest {
  payment_type: number
  amount: string | number
  idempotency_key: string
  /** Refund tenders: SOURCE receipt payment JT this refund settles
   *  (refund-integrity 2b). BE auto-binds when absent. */
  source_jt_id?: number
  /** Refund tenders: credit the SOURCE receipt's online (Moneris
   *  Checkout) capture at the gateway. The BE moves the money first,
   *  then books this JT with the refund transaction id. Mutually
   *  exclusive with source_jt_id (no JT exists behind an online
   *  capture). */
  online_refund?: boolean
  transaction_id?: string
  extra?: Record<string, unknown>
  notes?: string
  last4?: string
  card_info?: unknown
  gift_card_id?: number | null
  gift_card_number?: string
  gift_card_pin?: string
  receive_on_account?: boolean
  return_reason_text?: string | null
  return_reason_id?: number | null
  discount_type?: string | null
  reward_id?: number | null
  offer_id?: number | null
  /** Current local cart contents. When present, the BE truncate-and-
   *  replaces the receipt's cart from this list BEFORE charging -- FE
   *  owns "what items, how many", BE owns "what total". Eliminates the
   *  mid-payment add/remove/swap drift class. Shape matches
   *  cartToRequest(cart).items. */
  items?: CartItems
  // Cashier explicitly acknowledged a >$100 cash overpayment via the
  // FE confirm dialog. The BE keys on this to skip the cash-flow
  // overpayment gate; without it, the BE rejects with
  // overpayment_unconfirmed. Card paths ignore this field -- card
  // overpayment is unconditionally blocked at the BE.
  confirm_overpayment?: boolean
}

// CheckoutModal still consumes the response as `result.data?.receipt` and
// `result.status`, so we keep the v2 envelope shape on the way out.
export interface V2PaymentResponse {
  success: true
  data: {
    receipt: Receipt
  }
  status?: 'already_paid' | 'duplicate_transaction'
}

/**
 * Disposition the BE assigns to a cancel-payment call. Drives the
 * cashier-facing toast / confirm copy in CheckoutModal.
 *  - `completed`   System ran the refund (Moneris void / Birchmount /
 *                  SpringBig). No physical action required.
 *  - `manual_cash` Cash JT voided. Cashier physically hands cash back.
 *  - `manual_card` Card JT voided but no integrated refund (PMT3,
 *                  hand-keyed, MCORE). Cashier refunds on the standalone
 *                  terminal — confirm-first flow expected.
 */
export type RefundDisposition = 'completed' | 'manual_cash' | 'manual_card'

export interface V2VoidPaymentResponse {
  success: true
  data: {
    receipt: Receipt
    refund: RefundDisposition
    jt_id: number
    payment_type: number
    replay: boolean
  }
}

/**
 * Process cash payment via V2 endpoint
 */
export function useV2ProcessCashPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      amount,
      transactionId,
      notes,
      confirmOverpayment,
      sourceJtId,
      items,
    }: {
      receiptId: number
      amount: number
      transactionId?: string
      notes?: string
      confirmOverpayment?: boolean
      sourceJtId?: number
      items?: CartItems
    }): Promise<V2PaymentResponse> => {
      const payload: V2PaymentRequest = {
        payment_type: PAYMENT_TYPE_IDS.CASH,
        amount: amount.toFixed(2),
        idempotency_key: idempotencyKey(),
        transaction_id: transactionId,
        notes,
        ...(confirmOverpayment ? { confirm_overpayment: true } : {}),
        ...(sourceJtId ? { source_jt_id: sourceJtId } : {}),
        ...(items !== undefined ? { items } : {}),
      }
      return v2api.post<V2PaymentResponse>(v2(`/receipts/${receiptId}/payments`), payload)
    },
    onSuccess: (data) => {
      if (data.data?.receipt) {
        queryClient.setQueryData(receiptKeys.detail(data.data.receipt.id), data.data.receipt)
      }
    },
  })
}

/**
 * Process card payment via V2 endpoint
 */
export function useV2ProcessCardPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      paymentType,
      amount,
      last4,
      transactionId,
      cardInfo,
      notes,
      sourceJtId,
      onlineRefund,
      items,
    }: {
      receiptId: number
      paymentType: 213 | 215 | 216 | 217
      amount: number
      last4?: string
      transactionId?: string
      cardInfo?: unknown
      notes?: string
      sourceJtId?: number
      onlineRefund?: boolean
      items?: CartItems
    }): Promise<V2PaymentResponse> => {
      const payload: V2PaymentRequest = {
        payment_type: paymentType,
        amount: amount.toFixed(2),
        idempotency_key: idempotencyKey(),
        last4,
        transaction_id: transactionId,
        card_info: cardInfo,
        notes,
        ...(sourceJtId ? { source_jt_id: sourceJtId } : {}),
        // Online (Moneris Checkout) gateway credit — see V2PaymentRequest.
        ...(onlineRefund ? { online_refund: true } : {}),
        ...(items !== undefined ? { items } : {}),
      }
      return v2api.post<V2PaymentResponse>(v2(`/receipts/${receiptId}/payments`), payload)
    },
    onSuccess: (data) => {
      if (data.data?.receipt) {
        queryClient.setQueryData(receiptKeys.detail(data.data.receipt.id), data.data.receipt)
      }
    },
  })
}

/**
 * Process gift card payment via V2 endpoint
 */
export function useV2ProcessGiftCardPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      giftCardNumber,
      giftCardId,
      giftCardPin,
      amount,
      transactionId,
      notes,
      sourceJtId,
      items,
    }: {
      receiptId: number
      giftCardNumber?: string
      giftCardId?: number
      giftCardPin?: string
      amount: number
      transactionId?: string
      notes?: string
      sourceJtId?: number
      items?: CartItems
    }): Promise<V2PaymentResponse> => {
      const payload: V2PaymentRequest = {
        payment_type: PAYMENT_TYPE_IDS.GIFT_CARD,
        amount: amount.toFixed(2),
        idempotency_key: idempotencyKey(),
        gift_card_number: giftCardNumber,
        gift_card_id: giftCardId,
        gift_card_pin: giftCardPin,
        transaction_id: transactionId,
        notes,
        ...(sourceJtId ? { source_jt_id: sourceJtId } : {}),
        ...(items !== undefined ? { items } : {}),
      }
      return v2api.post<V2PaymentResponse>(v2(`/receipts/${receiptId}/payments`), payload)
    },
    onSuccess: (data) => {
      if (data.data?.receipt) {
        queryClient.setQueryData(receiptKeys.detail(data.data.receipt.id), data.data.receipt)
      }
    },
  })
}

/**
 * Apply store credit via V2 endpoint
 */
export function useV2ApplyStoreCredit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      amount,
      transactionId,
      notes,
      items,
    }: {
      receiptId: number
      amount: number
      transactionId?: string
      notes?: string
      items?: CartItems
    }): Promise<V2PaymentResponse> => {
      const payload: V2PaymentRequest = {
        payment_type: PAYMENT_TYPE_IDS.STORE_CREDIT,
        amount: amount.toFixed(2),
        idempotency_key: idempotencyKey(),
        transaction_id: transactionId,
        notes,
        ...(items !== undefined ? { items } : {}),
      }
      return v2api.post<V2PaymentResponse>(v2(`/receipts/${receiptId}/payments`), payload)
    },
    onSuccess: (data) => {
      if (data.data?.receipt) {
        queryClient.setQueryData(receiptKeys.detail(data.data.receipt.id), data.data.receipt)
      }
    },
  })
}

/**
 * Apply loyalty points via V2 endpoint
 */
export function useV2ApplyLoyaltyPoints() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      amount,
      rewardId,
      offerId,
      discountType,
      transactionId,
      notes,
      items,
    }: {
      receiptId: number
      amount: number
      rewardId?: number
      offerId?: number
      discountType?: string
      transactionId?: string
      notes?: string
      items?: CartItems
    }): Promise<V2PaymentResponse> => {
      const payload: V2PaymentRequest = {
        // SpringBig redemptions dispatch as SPRINGBIG_POINTS (299) so the
        // BE's springbig-specific hooks (% discount math at frontend.py:599,
        // void path at ops_payment.py:1143, clock-out reporting) fire. Type
        // 214 (POINTS) is the legacy in-house points system and skips
        // those branches.
        payment_type: PAYMENT_TYPE_IDS.SPRINGBIG_POINTS,
        amount: amount.toFixed(2),
        idempotency_key: idempotencyKey(),
        reward_id: rewardId,
        offer_id: offerId,
        discount_type: discountType,
        transaction_id: transactionId,
        notes,
        ...(items !== undefined ? { items } : {}),
      }
      return v2api.post<V2PaymentResponse>(v2(`/receipts/${receiptId}/payments`), payload)
    },
    onSuccess: (data) => {
      if (data.data?.receipt) {
        queryClient.setQueryData(receiptKeys.detail(data.data.receipt.id), data.data.receipt)
      }
    },
  })
}

/**
 * Cancel a single partial payment by JT id —
 * DELETE v2('/receipts/{id}/payments/{jt_id}'), no body.
 *
 * BE routes the refund by payment type and returns a `refund`
 * disposition the cashier UI uses to drive its post-cancel toast:
 *   - `completed`   Moneris void / Birchmount reverse / SpringBig cancel
 *                   already ran on the BE. Caller just shows confirmation.
 *   - `manual_cash` Cash JT voided. Caller prompts "Hand back $X".
 *   - `manual_card` Card JT voided with no integrated refund path.
 *                   Caller prompts the cashier to refund on the
 *                   standalone terminal physically.
 *
 * Naturally idempotent: a re-issued DELETE on an already-voided JT
 * returns the prior disposition with `replay=true`.
 */
export function useV2VoidPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      jtId,
    }: {
      receiptId: number
      jtId: number
    }): Promise<V2VoidPaymentResponse> => {
      return v2api.delete<V2VoidPaymentResponse>(
        v2(`/receipts/${receiptId}/payments/${jtId}`),
      )
    },
    onSuccess: (data) => {
      if (data.data?.receipt) {
        queryClient.setQueryData(receiptKeys.detail(data.data.receipt.id), data.data.receipt)
      }
    },
  })
}

/**
 * POST v2('/receipts/{id}/receive-on-account') — wrapped envelope with
 * idempotency_key required.
 */
export function useReceiveOnAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      receiptId,
      amount,
      notes,
    }: {
      receiptId: number
      amount: number
      notes?: string
    }): Promise<Receipt> => {
      const envelope = await v2api.post<V2Envelope<{ receipt: Receipt }>>(
        v2(`/receipts/${receiptId}/receive-on-account`),
        {
          amount: amount.toFixed(2),
          notes,
          idempotency_key: idempotencyKey(),
        },
      )
      return envelope.data.receipt
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}

/**
 * POST v2('/receipts/{id}/settle-reward') — close a refund whose entire
 * remaining balance is a store-funded SpringBig reward.
 *
 * A redeemed reward is not the customer's money, so it maps to no refund
 * method and every rail renders at "up to $0.00". Usually harmless — the
 * reward share rides along with a cash/card refund, which credits it back
 * to the store automatically. But when the reward is ALL that is left to
 * give back (a sale settled entirely by the reward, or a later return
 * whose rails an earlier return already used up) there is no tender to
 * ride and the refund cannot be completed at all. This is the way out.
 *
 * The BE computes the amount and refuses (`refund_rail_still_available`)
 * while any original payment method can still pay the customer, so this
 * can never be used to skip a real refund. No idempotency key: a repeat
 * call finds the receipt settled and refuses.
 */
export function useSettleRewardShare() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ receiptId }: { receiptId: number }): Promise<Receipt> => {
      const envelope = await v2api.post<V2Envelope<{ receipt: Receipt }>>(
        v2(`/receipts/${receiptId}/settle-reward`),
        {},
      )
      return envelope.data.receipt
    },
    onSuccess: (receipt) => {
      queryClient.setQueryData(receiptKeys.detail(receipt.id), receipt)
    },
  })
}

/**
 * PUT /ht/api/v2/stores/{sid}/registers/{rid}/transactions/{jt_id}/payment-type
 * — wrapped envelope returning {transaction_id, payment_type}.
 */
export function useChangePaymentType() {
  return useMutation({
    mutationFn: async ({
      transactionId,
      paymentType,
    }: {
      transactionId: number
      paymentType: number
    }): Promise<{ transaction_id: number; payment_type: number }> => {
      const envelope = await v2api.put<V2Envelope<{ transaction_id: number; payment_type: number }>>(
        v2(`/transactions/${transactionId}/payment-type`),
        { payment_type: paymentType },
      )
      return envelope.data
    },
  })
}

/**
 * Unified V2 payment hook that maintains the existing call-site interface
 * (CheckoutModal expects `result.data.receipt` + `result.status`).
 * Routes to the appropriate V2 hook based on payment_type.
 */
export function useV2ProcessPayment() {
  const queryClient = useQueryClient()
  const processCash = useV2ProcessCashPayment()
  const processCard = useV2ProcessCardPayment()
  const processGiftCard = useV2ProcessGiftCardPayment()
  const applyCredit = useV2ApplyStoreCredit()
  const applyPoints = useV2ApplyLoyaltyPoints()

  return useMutation({
    mutationFn: async ({
      receiptId,
      payment,
    }: {
      receiptId: number
      payment: {
        payment_type: number
        amount: number
        gift_card_number?: string
        gift_card_id?: number
        gift_card_pin?: string
        last4?: string
        reward_id?: number
        offer_id?: number
        discount_type?: string
        notes?: string
        // Cash-only: forwarded only when the cashier acknowledged the
        // FE's >$100 large-tender confirm dialog. Other payment types
        // ignore this field.
        confirm_overpayment?: boolean
        /** Current local cart contents. Plumbed through to the v2 payments
         *  endpoint as ``items[]`` so the BE truncate-and-replaces the
         *  cart from FE-authoritative state BEFORE charging. */
        items?: CartItems
        /** Refund tenders: source payment binding (refund-integrity 2b). */
        source_jt_id?: number
        /** Refund tenders: gateway credit to the online (Moneris
         *  Checkout) capture — rides the card branch (the wire
         *  payment_type is the online card's brand int). */
        online_refund?: boolean
      }
    }): Promise<V2PaymentResponse> => {
      const { payment_type, amount, gift_card_number, gift_card_id, gift_card_pin, last4, reward_id, offer_id, discount_type, notes, confirm_overpayment, items, source_jt_id, online_refund } = payment

      if (payment_type === PAYMENT_TYPE_IDS.CASH) {
        return processCash.mutateAsync({
          receiptId, amount, notes,
          confirmOverpayment: confirm_overpayment,
          sourceJtId: source_jt_id,
          items,
        })
      } else if (payment_type === PAYMENT_TYPE_IDS.GIFT_CARD) {
        return processGiftCard.mutateAsync({
          receiptId,
          amount,
          giftCardNumber: gift_card_number,
          giftCardId: gift_card_id,
          giftCardPin: gift_card_pin,
          notes,
          sourceJtId: source_jt_id,
          items,
        })
      } else if (payment_type === PAYMENT_TYPE_IDS.STORE_CREDIT) {
        return applyCredit.mutateAsync({ receiptId, amount, notes, items })
      } else if (payment_type === PAYMENT_TYPE_IDS.POINTS || payment_type === PAYMENT_TYPE_IDS.SPRINGBIG_POINTS) {
        return applyPoints.mutateAsync({
          receiptId,
          amount,
          rewardId: reward_id,
          offerId: offer_id,
          discountType: discount_type,
          notes,
          items,
        })
      } else {
        // Card payments (Visa, MC, Debit, Amex) — including the online
        // (Moneris Checkout) gateway credit, which wires the card's
        // brand int + online_refund.
        return processCard.mutateAsync({
          receiptId,
          paymentType: payment_type as 213 | 215 | 216 | 217,
          amount,
          sourceJtId: source_jt_id,
          onlineRefund: online_refund,
          last4,
          notes,
          items,
        })
      }
    },
    onSuccess: (data) => {
      if (data.data?.receipt) {
        queryClient.setQueryData(receiptKeys.detail(data.data.receipt.id), data.data.receipt)
      }
    },
  })
}

// ============================================================================
// Side-effect hooks (gift card balance lookup, drawer, print)
// ============================================================================

// POST /ht/api/v2/gift_cards/check_balance
export function useCheckGiftCardBalance() {
  return useMutation({
    mutationFn: async (cardNumber: string): Promise<GiftCardBalanceResponse> => {
      return v2api.post<GiftCardBalanceResponse>(
        v2Unscoped('/gift_cards/check_balance'),
        { card_number: cardNumber },
      )
    },
  })
}

// Cash drawer kick. Branches on `register.local_printing_enabled` AND
// the presence of the v2 Electron shell:
//
//   * **Local-print registers running the waio-windows-v2 shell** — fetch
//     the printer_type-correct drawer-open buffer from the BE
//     (`cmd: 'open_drawer_buffer'`) and POST it to the shell's local print
//     server at `/print` (the same path receipts use). The shell's `/drawer`
//     route (node-thermal-printer's openCashDrawer()) returns 200 but doesn't
//     actuate our hardware; `/print` does, because it preserves the raw kick
//     bytes. The BE builds the kick off the register's printer_type/drawer2,
//     so it works on every printer profile (EPSON, Star, …).
//
//   * **Everything else** (server-side print registers, OR the legacy v1
//     Windows wrapper which only exposes `/print` on 9999) — POST
//     `cmd: 'open_drawer_only'` to v2('/command'), which routes through
//     the BE's configured printer. On legacy v1+local_printing_enabled
//     this still won't physically fire the drawer (the BE has no path
//     to localhost:9999 and the v1 wrapper strips ESC/POS control bytes
//     anyway), but it preserves the existing toast/UX rather than
//     introducing a 404 from a /drawer route the v1 wrapper doesn't have.
export function useOpenCashDrawer() {
  return useMutation({
    mutationFn: async (): Promise<{ status: string }> => {
      const { selectedRegister } = useSessionStore.getState()
      if (selectedRegister?.local_printing_enabled && getElectronShell()) {
        // Local-print registers run the print server on the till, which the
        // BE can't reach -- so fetch the drawer-open buffer from the BE and
        // POST it to the shell's /print path (the proven route receipts use;
        // /drawer returns 200 but doesn't actuate our hardware).
        //
        // The buffer MUST come from the BE so it honors the register's
        // printer_type. Hardcoding EPSON `ESC p` bytes here printed "006106"
        // -- the command's leftover ASCII params rendered as text -- and never
        // kicked on non-EPSON printers, which don't recognize the sequence.
        // cmd_open_drawer_buffer builds the correct bytes off the register's
        // printer_type/drawer2 (same source as the receipt kick).
        const drawer = await v2api.post<{ status?: string } & PrintableBufferResponse>(
          v2('/command'), { cmd: 'open_drawer_buffer' },
        )
        await sendBufferToLocalPrinter({
          buffer: drawer.buffer,
          binary: !!drawer.binary,
          type: drawer.type ?? 'text',
        })
        return { status: 'ok' }
      }
      return v2api.post(v2('/command'), { cmd: 'open_drawer_only' })
    },
  })
}

// ---------------------------------------------------------------------------
// Receipt printing
// ---------------------------------------------------------------------------
//
// Two paths, picked by `register.local_printing_enabled`:
//
//   * **Local print** (Windows wrapper deploy): GET the printable buffer
//     from the BE, then POST it to the local Windows print service
//     running on http://localhost:9999/print. Mirrors the v1 controllers.js
//     local-printing flow.
//
//   * **Server-side print** (default): POST /v2/.../receipts/{id}/print
//     and let the BE talk to the printer it has configured.
//
// The Windows service URL and timeout are constants here -- they match
// the v1 hardcoded values. If a deploy needs a different port, expose a
// VITE_LOCAL_PRINT_URL env var and read that in `LOCAL_PRINT_URL` below.

const LOCAL_PRINT_URL =
  (typeof import.meta !== 'undefined'
    && (import.meta as { env?: { VITE_LOCAL_PRINT_URL?: string } }).env?.VITE_LOCAL_PRINT_URL)
  || 'http://localhost:9999/print'

const LOCAL_PRINT_TIMEOUT_MS = 15000

interface PrintableBufferResponse {
  buffer: string
  buffer2?: string | null
  binary?: boolean
  type?: string
}

/**
 * Send the receipt's printable buffer to the local Windows print service.
 * Throws on transport failure so the caller can surface the error.
 *
 * The body is a JSON string of the buffer response (matching v1
 * controllers.js which sends `JSON.stringify(response)` with content-type
 * `text/plain`). The service tolerates both shapes.
 */
async function sendBufferToLocalPrinter(
  buffer: PrintableBufferResponse,
): Promise<void> {
  const ctrl = new AbortController()
  const timer = window.setTimeout(() => ctrl.abort(), LOCAL_PRINT_TIMEOUT_MS)
  try {
    const res = await fetch(LOCAL_PRINT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      // Match the v1 wire format -- the service expects the BE's full
      // buffer response object, not just `buffer.buffer`.
      body: JSON.stringify(buffer),
      signal: ctrl.signal,
    })
    if (!res.ok) {
      throw new Error(`Local print service returned ${res.status}`)
    }
  } finally {
    window.clearTimeout(timer)
  }
}

/**
 * Send a receipt's print job through whichever path the register is wired
 * for. When `selectedRegister.local_printing_enabled` is true we GET the
 * BE's printable buffer and POST it to the Windows print service;
 * otherwise the BE drives its own printer via the named server endpoint.
 *
 * The `serverEndpoint` switch covers the two server-side variants: the
 * default `print` endpoint is used after pay/reprint, while `print-on-hold`
 * is used for hold slips (which the BE renders with on-hold styling).
 * Local printing uses the same /printable-buffer for either case -- the
 * buffer reflects the receipt's current on-hold flag.
 *
 * Branches on the register flag at call time so the cashier can toggle
 * registers mid-shift without the print path getting stuck.
 */
export async function printReceiptViaPreferredPath({
  receiptId,
  isCopy = false,
  serverEndpoint = 'print',
}: {
  receiptId: number
  isCopy?: boolean
  serverEndpoint?: 'print' | 'print-on-hold'
}): Promise<void> {
  const { selectedRegister } = useSessionStore.getState()
  const localPrint = !!selectedRegister?.local_printing_enabled

  if (localPrint) {
    // Local-print path: pull the buffer, send to the Windows service.
    // The BE's /printable-buffer call is read-only -- safe to retry.
    // We DON'T fall back to server-side on local failure; the cashier
    // is sitting at a Windows wrapper, the BE printer isn't theirs.
    const buffer = await v2api.get<PrintableBufferResponse>(
      v2(`/receipts/${receiptId}/printable-buffer`),
      isCopy ? { is_copy: 1 } : undefined,
    )
    await sendBufferToLocalPrinter(buffer)
    return
  }

  // Server-side path: BE drives the printer it has configured.
  const body = serverEndpoint === 'print' ? { is_copy: isCopy } : {}
  await v2api.post(v2(`/receipts/${receiptId}/${serverEndpoint}`), body)
}

/**
 * Print a receipt. Branches on the selected register's
 * `local_printing_enabled` flag at call time -- the cashier can toggle
 * registers without a remount and still get the right path.
 */
export function usePrintReceipt() {
  return useMutation({
    mutationFn: async ({
      receiptId,
      isCopy = false,
    }: {
      receiptId: number
      isCopy?: boolean
    }): Promise<{ status: string }> => {
      await printReceiptViaPreferredPath({ receiptId, isCopy })
      return { status: 'ok' }
    },
  })
}
